import { CreateProcurementTaskFormDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { OneCreationActionResolver } from 'modules/core/lib/oneCreateActions/types'

import { FromQuestionaireToMutationVariables } from 'modules/forms/components/newFormEditor/helpers'
import { AgentStateType, QuestionType } from 'modules/forms/components/newFormEditor/types'
import { useUpdateNewForm } from 'modules/forms/hooks/useForms'

type Question = {
  label: string
  type: QuestionType['type']
  mandatory?: boolean
  options?: Array<{ label: string; value: string }>
}

export const useCreateProcurementTaskForm: OneCreationActionResolver = () => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const { updateForm } = useUpdateNewForm()

  const [createProcurementTaskForm, { loading }] = useMutation(CreateProcurementTaskFormDocument, {
    extractorFn: (data) => data.procurementMutations.createProcurementTaskForm,
    errorPolicy: 'all',
  })

  const createTaskFormHandler = async (args: {
    name: string
    category?: 'basic' | 'provider'
    questions?: Question[]
  }) => {
    const name = args.name.trim()
    const category = args.category ?? 'basic'

    const taskFormResult = await createProcurementTaskForm({
      variables: {
        name,
        category,
        companyId: company.id,
      },
    })

    const handled = handleResult(taskFormResult, (_errors) => {})

    if (!handled.success || !handled.data.taskForm?.id) {
      return handled
    }

    const taskFormId = handled.data.taskForm.id
    const formId = handled.data.taskForm.formsConnection?.edges?.[0]?.node?.id

    const mappedQuestions: QuestionType[] =
      args.questions && args.questions.length > 0
        ? args.questions.map((q, i) => ({
            id: undefined,
            label: q.label,
            type: q.type,
            mandatory: q.mandatory ?? false,
            position: i,
            description: '',
            locked: false,
            params: {},
            hasCorrectAnswer: false,
            options: q.options ? q.options.map((o) => ({ ...o, isCorrect: false })) : null,
          }))
        : []

    if (mappedQuestions.length > 0 && formId) {
      await updateForm({
        ...FromQuestionaireToMutationVariables(formId, false, {
          title: name,
          description: '',
          elements: mappedQuestions,
        }),
      })
    }

    const coCreationData: AgentStateType = {
      questionnaire: {
        title: name,
        description: '',
        elements: mappedQuestions,
      },
      updateForm: false,
    }

    return {
      to: navigation.settings.procurement.taskForms.id.edit.fullPath({ id: taskFormId }),
      data: coCreationData,
      followUp:
        'The task form has been created and the user is now on the detail page. Do NOT call createProcurementTaskForm again. If the user wants to modify questions, use createSurveyQuestions, updateSurveyQuestions, or deleteSurveyQuestions.',
    }
  }

  return {
    resolver: createTaskFormHandler,
    loading,
  }
}
